import { useEffect, useState } from "react";
import axios from "axios";
import { Users, AlertTriangle } from "lucide-react";

/** Live staff-to-child ratio (children signed in against staff clocked in, per site). `tone` colours: ok green, tight amber, over red. */
export const RATIO_TONE = {
  ok: "text-[var(--hp-profit)] border-[var(--hp-profit)]",
  tight: "text-[var(--hp-gold)] border-[var(--hp-gold)]",
  over: "text-[var(--hp-loss)] border-[var(--hp-loss)]",
  unknown: "text-[var(--hp-muted)] border-[var(--hp-border)]",
};
export const ratioFill = (s) => ({ ok: "var(--hp-profit)", tight: "var(--hp-gold)", over: "var(--hp-loss)" }[s] || "var(--hp-muted)");

/** Polls the tablet's own ratio. Quiet on failure (the tablet may be offline): the last answer stays until a newer one arrives. */
export function useKioskRatio(api, active, every = 30000) {
  const [r, setR] = useState(null);
  useEffect(() => {
    if (!active || !api) return undefined;
    let stop = false;
    const go = () => axios.get(`${api}/kiosk/ratio`, { silent: true }).then((x) => { if (!stop) setR(x.data); }).catch(() => {});
    go();
    const id = setInterval(go, every);
    return () => { stop = true; clearInterval(id); };
  }, [api, active, every]);
  return r;
}

/** The small chip in the tablet's header: "9 children · 1 staff". Shown whenever staff are being clocked in, so a tight moment isn't a surprise. */
export function RatioPill({ ratio }) {
  if (!ratio || ratio.status === "unknown") return null;
  const t = RATIO_TONE[ratio.status];
  return (
    <span data-testid="kiosk-ratio-pill" data-status={ratio.status} title={ratio.message}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${t}`}
      style={{ background: `color-mix(in srgb, ${ratioFill(ratio.status)} ${ratio.status === "ok" ? 8 : 16}%, transparent)` }}>
      {ratio.status === "ok" ? <Users className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
      {ratio.children} {ratio.children === 1 ? "child" : "children"} · {ratio.staff} staff
    </span>
  );
}

/** A full-width notice for the staff area and the Today screen when numbers are tight or over, with one line per site. */
export function RatioBanner({ ratio, testId = "ratio-banner" }) {
  if (!ratio || !(ratio.status === "tight" || ratio.status === "over")) return null;
  const t = RATIO_TONE[ratio.status];
  const flagged = (ratio.sites || []).filter((s) => s.status === "tight" || s.status === "over");
  return (
    <div role="alert" data-testid={testId} data-status={ratio.status} className={`rounded-xl border px-4 py-3 text-sm ${t}`}
      style={{ background: `color-mix(in srgb, ${ratioFill(ratio.status)} 12%, transparent)` }}>
      <p className="flex items-start gap-2 font-semibold"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {ratio.status === "over" ? "Ratio exceeded" : "Ratio getting tight"}</p>
      <ul className="mt-1 space-y-0.5 text-[var(--hp-text)]">
        {(flagged.length ? flagged : [ratio]).map((s, i) => <li key={s.siteId || i}>{s.siteName ? <strong>{s.siteName}: </strong> : null}{s.message}</li>)}
      </ul>
    </div>
  );
}

/** Today's card: the numbers for each site, always visible once staff are being clocked in. */
export function RatioCard({ ratio }) {
  if (!ratio || ratio.status === "unknown") return null;
  return (
    <section className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5 space-y-3" data-testid="today-ratio" data-status={ratio.status}>
      <h2 className="font-head text-lg font-semibold text-[var(--hp-heading)] inline-flex items-center gap-2"><Users className="h-4 w-4 text-[var(--hp-profit)]" /> Staff-to-child ratio, right now</h2>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {ratio.sites.map((s, i) => (
          <div key={s.siteId || i} data-testid={`today-ratio-site-${i}`} data-status={s.status} className={`rounded-xl border px-4 py-3 ${RATIO_TONE[s.status]}`}
            style={{ background: `color-mix(in srgb, ${ratioFill(s.status)} ${s.status === "ok" ? 5 : 12}%, transparent)` }}>
            {s.siteName ? <p className="text-xs uppercase tracking-wider text-[var(--hp-muted)]">{s.siteName}</p> : null}
            <p className="font-mono-num text-2xl font-bold">{s.children} <span className="text-sm font-semibold text-[var(--hp-muted)]">{s.children === 1 ? "child" : "children"}</span> · {s.staff} <span className="text-sm font-semibold text-[var(--hp-muted)]">staff</span></p>
            <p className="text-xs text-[var(--hp-text)]">{s.message}</p>
            {s.expected > 0 && s.status !== "tight" ? <p className="text-xs text-[var(--hp-muted)]">{s.expected} more due within the hour</p> : null}
          </div>))}
      </div>
      <p className="text-xs text-[var(--hp-muted)]">Children signed in on the tablet and not yet out, against staff clocked in on it, using your ratios (Settings, Ratios). A prompt to look, not a licensing decision.</p>
    </section>
  );
}
